/**
 * CLE-2786 -- one contract shared by several providers.
 *
 * The contract's own provider is the PRIMARY. `linkedProviderIds` lists the other providers
 * -- usually in other accounts -- that draw their rates from this contract instead of from a
 * hand-maintained duplicate of it. Ani's case: "Ascentist Physician Group" exists as six
 * provider records across six accounts, five of which held byte-identical contracts.
 *
 * Mirrors `services/clear-api/src/domains/_shared/linkedProviders.ts`; keep the two in step.
 */

export interface ILinkedProvider {
  id: string;
  name: string;
  accountId?: string | null;
}

export interface ILinkedProviderOption {
  label: string;
  value: string;
}

export const LINKED_PROVIDERS_COPY = {
  title: "Connected Providers",
  placeholder: "Select connected providers",
  // One line, current: rates are generated on save (CLE-2800); a provider that already has a
  // contract is offered Replace (CLE-2789).
  note: "Each connected provider gets these rates under its own name, generated when you save. If it already has a contract for this payer, you'll be offered to replace it.",
};

/**
 * Options for the connected-providers picker: every provider except the contract's own.
 * Deliberately NOT filtered to the current account -- linking across accounts is the point.
 *
 * CLE-2800 -- the providers already connected to the contract are always offered too, by the
 * name the contract API returned. The picker renders a selected id it has no option for as a
 * blank chip with only an "x", which is how Ani removed a provider without knowing which one.
 */
export const linkedProviderOptions = (
  providers: ILinkedProvider[] | undefined,
  contractProviderId: string | undefined,
  alreadyLinked: ILinkedProvider[] | undefined = undefined
): ILinkedProviderOption[] => {
  const byId = new Map<string, ILinkedProviderOption>();
  [...(providers ?? []), ...(alreadyLinked ?? [])]
    .filter((p) => p.id !== contractProviderId && !byId.has(p.id))
    .forEach((p) => byId.set(p.id, { label: p.name, value: p.id }));
  return [...byId.values()].sort((a, b) => a.label.localeCompare(b.label));
};

/** CLE-2800 -- the connected providers a save would drop (and whose rows it would delete). */
export const providersRemoved = (
  before: string[] | undefined,
  after: string[] | undefined
): string[] => (before ?? []).filter((id) => !(after ?? []).includes(id));

/** CLE-2800 -- the providers a save connects for the first time (rows generated on save). */
export const providersAdded = (
  before: string[] | undefined,
  after: string[] | undefined
): string[] => (after ?? []).filter((id) => !(before ?? []).includes(id));

/**
 * Copy for the disconnect confirmation (CLE-2800). Ani removed a provider by hitting a chip's
 * "x" without noticing and the save deleted every one of its rates with no warning; the
 * dialog names the providers and says what the save does to them.
 */
export const disconnectConfirmCopy = (
  removed: ILinkedProvider[],
  payerName: string | undefined
): { names: string; body: string } => {
  const plural = removed.length > 1;
  return {
    names: removed.map((p) => p.name).join(", "),
    body: `${
      plural ? "Their" : "Its"
    } rates from this contract will be deleted when you save, and ${
      plural ? "they" : "it"
    } will have no rates for ${
      payerName ?? "this payer"
    } until connected again or given ${plural ? "their" : "its"} own contract.`,
  };
};

/**
 * Names of the providers sharing a contract, for the list and detail views. The primary comes
 * first, matching how the contract is stored and how Ani asked to see it ("the primary one
 * with connected names").
 */
export const sharedProviderNames = (
  primaryName: string | undefined,
  linkedProviders: ILinkedProvider[] | undefined
): string[] => [
  ...(primaryName ? [primaryName] : []),
  ...(linkedProviders ?? []).map((p) => p.name),
];

/**
 * Keeps a selection honest when the contract's own provider changes underneath it: the new
 * provider can never also be a connected one.
 */
export const withoutContractProvider = (
  linkedProviderIds: string[] | undefined,
  contractProviderId: string | undefined
): string[] =>
  (linkedProviderIds ?? []).filter((id) => id !== contractProviderId);

/**
 * CLE-2789 -- the API's refusal when a connected provider still holds its own contract for
 * the payer. Used ONLY as the trigger to fetch structured conflicts from the API -- never to
 * derive provider ids from the text: Ascentist alone has six near-identical provider names.
 */
export const isOwnContractConflictMessage = (
  message: string | undefined
): boolean => /already has its own active contract/i.test(message ?? "");

export interface IOwnContractConflict {
  providerId: string;
  providerName: string;
  contractIds: string[];
  /** Providers connected to the old contract. Replace moves them to the new one as well. */
  connectedProviders?: { id: string; name: string }[];
}

export const REPLACE_NEEDS_ACTIVE_CONTRACT =
  "This contract is a draft, so it has no rates to give a connected provider yet. Activate it first, then connect the provider and choose Replace.";

/**
 * Copy for the connect-and-replace confirmation. Says what will happen and in what order,
 * because the order is the safety: new rates written first, old contract deleted second.
 */
export const replaceConfirmCopy = (
  conflicts: IOwnContractConflict[]
): { names: string; title: string; body: string; carried: string } => {
  const names = conflicts.map((c) => c.providerName).join(", ");
  // The old contract's own connected providers come along, so nobody is left with no rates.
  // Named here so the user knows the change reaches further than the provider they picked.
  const carriedNames = conflicts
    .flatMap((c) => c.connectedProviders ?? [])
    .map((p) => p.name);
  const carried =
    carriedNames.length === 0
      ? ""
      : `That contract also covers ${carriedNames.join(", ")} — ${
          carriedNames.length === 1 ? "it moves" : "they move"
        } to this contract as well.`;
  const title =
    conflicts.length === 1
      ? "already has its own contract for this payer."
      : "already have their own contracts for this payer.";
  return {
    names,
    title,
    body: "Replace with this contract? Each provider's new rates are written first, then its old contract is deleted, so there is never a moment without rates.",
    carried,
  };
};
